// ============================================================================
// STORE.REWORK.JS — Missing Items Requests + Outreach Touch Log (Stage 3)
//
// Schema v2 (flat, one doc per missing item type):
//   reworkRequests/{requestId}  — one flat doc per missing item, NOT an items[] array
//   reworkTouches/{touchId}     — append-only outreach event
//   workflowEvents/{eventId}    — shared audit trail (Stage 2+)
//   workflow/state              — singleton workflow state
//
// createReworkRequest(tenantId, referralId, currentWorkflow, selectedItemTypes, uid, role)
//   Writes ONE reworkRequest doc per item type in a single batch.
//   Also writes workflow/state (status = NEEDS_INFO) + one workflowEvent.
//   No serverTimestamp() inside arrays. No items[] sub-array.
//
// Auto-resolution (system automation):
//   When an attachment manifest becomes READY, the Cloud Function
//   onAttachmentManifestReady_updateRework matches attachment type → item type,
//   marks matching reworkRequest.status = 'received', and if all requests are
//   satisfied, transitions workflow/state NEEDS_INFO → READY_TO_SCHEDULE.
//   Intake should NOT manually mark items received — the upload is the truth signal.
//
// Zero-PHI invariants:
//   • No patient name / DOB / MRN / contact / file names in any rework doc.
//   • All types, statuses, channels, outcomes are controlled enums.
//
// Stage 3 Invariants:
//   1. createReworkRequest requires non-null currentWorkflow.
//   2. Every createReworkRequest/logReworkTouch creates exactly one workflowEvent.
//   3. One reworkRequest doc per item type — never a multi-item array doc.
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  getDocs,
  collection,
  query,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';
import { WORKFLOW_STATUS } from './store.workflow.js';

// ── DB singleton ──────────────────────────────────────────────────────────────

function getDb() {
  return getFirestore(getApp());
}

// ── ID generators ─────────────────────────────────────────────────────────────

function generateRequestId() {
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function generateTouchId() {
  return `tch_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function generateEventId() {
  return `wfe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Top-level status of a rework request doc (one doc = one item type).
 * OPEN     = intake requested it, GP has not yet uploaded.
 * RECEIVED = auto-marked by system when attachment manifest becomes READY.
 */
export const REWORK_REQUEST_STATUS = {
  OPEN:      'open',
  RECEIVED:  'received',
  // kept for backward compatibility with schema v1 docs
  RESOLVED:  'resolved',
  CANCELLED: 'cancelled',
};

export const REWORK_ITEM_TYPE = {
  LABS:           'LABS',
  IMAGING_REPORT: 'IMAGING_REPORT',
  REFERRAL_NOTE:  'REFERRAL_NOTE',
  INSURANCE:      'INSURANCE',
  MED_LIST:       'MED_LIST',
  OTHER_DOC:      'OTHER_DOC',
};

/** Human-readable labels for UI (both sides: intake + GP). */
export const REWORK_ITEM_TYPE_LABELS = {
  LABS:           'Lab Results',
  IMAGING_REPORT: 'Imaging Report',
  REFERRAL_NOTE:  'Referral Note',
  INSURANCE:      'Insurance Info',
  MED_LIST:       'Medications List',
  OTHER_DOC:      'Other Document',
};

/** Kept for backward compat with any existing per-item status data. Not used in v2 writes. */
export const REWORK_ITEM_STATUS = {
  MISSING:   'MISSING',
  REQUESTED: 'REQUESTED',
  PROVIDED:  'PROVIDED',
  RECEIVED:  'RECEIVED',
};

export const REWORK_TOUCH_CHANNEL = {
  PHONE:     'PHONE',
  FAX:       'FAX',
  PORTAL:    'PORTAL',
  IN_PERSON: 'IN_PERSON',
  OTHER:     'OTHER',
};

export const REWORK_TOUCH_DIRECTION = {
  OUTBOUND: 'OUTBOUND',
  INBOUND:  'INBOUND',
};

export const REWORK_TOUCH_OUTCOME = {
  NO_ANSWER:     'NO_ANSWER',
  LEFT_MESSAGE:  'LEFT_MESSAGE',
  SENT_REQUEST:  'SENT_REQUEST',
  RECEIVED_INFO: 'RECEIVED_INFO',
  OTHER:         'OTHER',
};

export const REWORK_EVENT_TYPES = {
  REWORK_REQUEST_CREATED:    'REWORK_REQUEST_CREATED',
  REWORK_TOUCH_LOGGED:       'REWORK_TOUCH_LOGGED',
  // kept for event history display
  REWORK_ITEM_STATUS_CHANGED:'REWORK_ITEM_STATUS_CHANGED',
  REWORK_RESOLVED:           'REWORK_RESOLVED',
};

// ============================================================================
// BUILDERS
// ============================================================================

/**
 * Build a flat rework request doc (schema v2).
 * One doc per item type. No items[] array. No serverTimestamp in arrays.
 */
export function buildReworkRequestDoc({
  tenantId,
  referralId,
  requestId,
  itemType,
  createdByUid,
  createdByRole,
}) {
  return {
    tenantId,
    referralId,
    requestId,
    itemType,
    status:        REWORK_REQUEST_STATUS.OPEN,
    createdByUid,
    createdByRole,
    createdAt:     null, // set by serverTimestamp in batch
    updatedAt:     null, // set by serverTimestamp in batch
    resolvedAt:    null,
    schemaVersion: 2,
  };
}

/**
 * Build a rework touch document (append-only outreach event).
 */
export function buildReworkTouch({
  tenantId,
  referralId,
  touchId,
  channel,
  direction,
  outcome,
  actorUid,
  actorRole,
}) {
  return {
    tenantId,
    referralId,
    touchId,
    channel,
    direction,
    outcome,
    actorUid,
    actorRole,
    createdAt: null, // set by serverTimestamp in batch
  };
}

/**
 * Build a workflow event for rework actions.
 */
export function buildReworkEvent({
  tenantId,
  referralId,
  eventType,
  actorUid,
  actorRole,
  requestId   = null,
  itemType    = null,
}) {
  return {
    tenantId,
    referralId,
    eventType,
    actorUid,
    actorRole,
    requestId,
    itemType,
    createdAt: null, // set by serverTimestamp in batch
  };
}

// ============================================================================
// READ FUNCTIONS
// ============================================================================

/**
 * List all rework request docs for a referral (newest first).
 * Each doc is a flat record for a single item type (schema v2).
 * Backward-compat: schema v1 docs with items[] are returned as-is.
 */
export async function listReworkRequests(tenantId, referralId, maxResults = 50) {
  const db = getDb();
  const q = query(
    collection(db, PATHS.col.reworkRequests(tenantId, referralId)),
    orderBy('createdAt', 'desc'),
    limit(maxResults),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * List all rework touches for a referral (newest first).
 */
export async function listReworkTouches(tenantId, referralId, maxResults = 30) {
  const db = getDb();
  const q = query(
    collection(db, PATHS.col.reworkTouches(tenantId, referralId)),
    orderBy('createdAt', 'desc'),
    limit(maxResults),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ============================================================================
// WRITE FUNCTIONS
// ============================================================================

/**
 * Create missing-item requests for a referral.
 *
 * Schema v2: creates ONE flat reworkRequest doc per selected item type.
 * No items[] array. No serverTimestamp inside arrays.
 *
 * Batch contents (for N selected item types):
 *   1 × N   reworkRequests/{requestId_i}  CREATE  (one doc per item type)
 *   1       workflow/state                UPDATE  (status = NEEDS_INFO, missingItemsCount += N)
 *   1       workflowEvents/{eventId}      CREATE  (REWORK_REQUEST_CREATED)
 *
 * Invariant: currentWorkflow must be non-null (workflow/state must exist).
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {object}   currentWorkflow   — current workflow/state doc
 * @param {string[]} selectedItemTypes — one or more REWORK_ITEM_TYPE values
 * @param {string}   uid               — actor uid (intake coordinator)
 * @param {string}   role              — actor role
 */
export async function createReworkRequest(
  tenantId,
  referralId,
  currentWorkflow,
  selectedItemTypes,
  uid,
  role,
) {
  if (!currentWorkflow) {
    throw new Error(
      'Invalid system state: workflow/state document is missing. Cannot create rework request.'
    );
  }
  if (!selectedItemTypes || selectedItemTypes.length === 0) {
    throw new Error('Select at least one missing item type.');
  }
  // Ownership check — an intake_coordinator must claim the referral before requesting
  // missing items, since this moves the referral into NEEDS_INFO. Mirrors the Firestore rule.
  if (role === 'intake_coordinator' && currentWorkflow.ownerUid !== uid) {
    throw new Error('Claim this referral before requesting missing items.');
  }

  const db      = getDb();
  const now     = serverTimestamp();
  const eventId = generateEventId();

  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));

  // Build one doc per item type
  const requestDocs = selectedItemTypes.map((itemType) => {
    const requestId  = generateRequestId();
    const requestRef = doc(db, PATHS.reworkRequest(tenantId, referralId, requestId));
    const requestDoc = buildReworkRequestDoc({
      tenantId,
      referralId,
      requestId,
      itemType,
      createdByUid:  uid,
      createdByRole: role,
    });
    return { requestRef, requestDoc };
  });

  const eventDoc = buildReworkEvent({
    tenantId,
    referralId,
    eventType:  REWORK_EVENT_TYPES.REWORK_REQUEST_CREATED,
    actorUid:   uid,
    actorRole:  role,
    itemType:   selectedItemTypes.join(','), // comma-separated summary for event log
  });

  const batch = writeBatch(db);

  // 1. Create one reworkRequest doc per item type
  for (const { requestRef, requestDoc } of requestDocs) {
    batch.set(requestRef, { ...requestDoc, createdAt: now, updatedAt: now });
  }

  // 2. Update workflow/state: status = NEEDS_INFO, increment missingItemsCount, set statusEnteredAt
  const wasAlreadyNeedsInfo = currentWorkflow.status === WORKFLOW_STATUS.NEEDS_INFO;
  const workflowUpdate = {
    'rework.missingItemsCount': increment(selectedItemTypes.length),
    updatedAt:                  now,
    updatedByUid:               uid,
  };
  if (!wasAlreadyNeedsInfo) {
    workflowUpdate.status          = WORKFLOW_STATUS.NEEDS_INFO;
    workflowUpdate.statusEnteredAt = now;
  }
  batch.update(stateRef, workflowUpdate);

  // 2b. Denormalize the Not-Yet-Inbox query fields onto the parent referral doc.
  //   • awaitingGpDocs = true — there is now >=1 OPEN document request the GP must
  //     upload, so this referral belongs in the "Docs Requested" lane. This is the
  //     discriminator that keeps Not-Yet *recovery* referrals (which carry NO
  //     document requests) out of that lane. Set on every request batch (even when
  //     already NEEDS_INFO); cleared by the attachment-ready Cloud Function when the
  //     last open request is received.
  //   • workflowStatus + workflowStatusEnteredAt — only on the NEW → NEEDS_INFO
  //     transition (mirrors commitWorkflowAction).
  // Canonical source of truth remains workflow/state. The affectedKeys() allowlist
  // in firestore.rules permits exactly these three keys.
  const referralRef    = doc(db, PATHS.referral(tenantId, referralId));
  const referralUpdate = { awaitingGpDocs: true };
  if (!wasAlreadyNeedsInfo) {
    referralUpdate.workflowStatus          = WORKFLOW_STATUS.NEEDS_INFO;
    referralUpdate.workflowStatusEnteredAt = now;
  }
  batch.update(referralRef, referralUpdate);

  // 3. Append workflowEvent
  batch.set(eventRef, { ...eventDoc, createdAt: now });

  await batch.commit();

  return requestDocs.map(({ requestDoc }) => requestDoc.requestId);
}

/**
 * Log a rework touch (outreach attempt) for a referral.
 * Increments workflow/state.rework.attempts and sets lastTouchAt.
 * This is an activity trace only — it does NOT resolve any request.
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {object} currentWorkflow — current workflow/state doc
 * @param {string} channel         — REWORK_TOUCH_CHANNEL value
 * @param {string} direction       — REWORK_TOUCH_DIRECTION value
 * @param {string} outcome         — REWORK_TOUCH_OUTCOME value
 * @param {string} uid             — actor uid
 * @param {string} role            — actor role
 */
export async function logReworkTouch(
  tenantId,
  referralId,
  currentWorkflow,
  channel,
  direction,
  outcome,
  uid,
  role,
) {
  const db      = getDb();
  const now     = serverTimestamp();
  const touchId = generateTouchId();
  const eventId = generateEventId();

  const touchRef = doc(db, PATHS.reworkTouch(tenantId, referralId, touchId));
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));

  const touchDoc = buildReworkTouch({
    tenantId, referralId, touchId,
    channel, direction, outcome,
    actorUid:  uid,
    actorRole: role,
  });

  const eventDoc = buildReworkEvent({
    tenantId, referralId,
    eventType:  REWORK_EVENT_TYPES.REWORK_TOUCH_LOGGED,
    actorUid:   uid,
    actorRole:  role,
  });

  const batch = writeBatch(db);

  // 1. Create touch doc (append-only)
  batch.set(touchRef, { ...touchDoc, createdAt: now });

  // 2. Update workflow/state: increment attempts + set lastTouchAt
  batch.update(stateRef, {
    'rework.attempts':    increment(1),
    'rework.lastTouchAt': now,
    updatedAt:            now,
    updatedByUid:         uid,
  });

  // 3. Append workflowEvent
  batch.set(eventRef, { ...eventDoc, createdAt: now });

  await batch.commit();
  return touchId;
}
